<i18n locale="en" lang="json">
{
  "title": "Error message",
  "ok": "Ok"
}
</i18n>
<i18n locale="nl" lang="json">
{
  "title": "Foutmelding",
  "ok": "Ok"
}
</i18n>

<template>
  <base-dialog
    ref="dialog"
    v-model:is-open="internalIsOpen"
    :title="title || t('title')"
    width="400"
    test-id="BaseErrorDialog"
  >
    <slot name="default">
      <div class="font-weight-bold">{{ computedMessage }}</div>
    </slot>
    <template #actions="{ resolve }">
      <base-button
        test-id="error-dialog-ok"
        primary
        @click="resolve(undefined)"
        >{{ t('ok') }}</base-button
      >
    </template>
  </base-dialog>
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import type {
  BaseDialogType,
  DialogEmits,
  DialogProps,
} from '@/common/components/zvdzo/BaseDialog.types';
import useDialogIsOpen from '@/common/composition/useDialogIsOpen';
import type { KnownExceptionKey } from '@/types';
import camelCase from '@/common/camel-case';

type Props = DialogProps & {
  knownErrorKey?: keyof KnownExceptionKey;
  metaData?: Record<string, unknown>;
  title?: string;
  message?: string;
};
const props = defineProps<Props>();
const emit = defineEmits<DialogEmits>();

const { t } = useI18n();

const internalIsOpen = useDialogIsOpen(props, emit);

const dialog = ref<BaseDialogType>();

const open = async () => {
  if (!dialog.value) {
    throw new Error('no dialog');
  }
  await dialog.value.open();
};

const knownErrori18nKey = computed<string | undefined>(() =>
  props.knownErrorKey
    ? `knownError.${camelCase(props.knownErrorKey)}`
    : undefined,
);

const computedMessage = computed(() => {
  if (knownErrori18nKey.value) {
    return (
      t(knownErrori18nKey.value, props.metaData || {}) ||
      props.message ||
      t('knownError.generalServerError')
    );
  }
  return (
    props.message || t('knownError.generalServerError', props.metaData || {})
  );
});

defineExpose({ open });
</script>
